import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DetailContext,
  DetailMode,
  EvidenceContext,
  FormPanel,
} from '@eas/common-web';
import { proceedingsSubjectsLocalTreeUrl } from '@modules/dict-proceedings-subject/dict-proceedings-subject-api';
import { subjectIndexesLocalTreeUrl } from '@modules/dict-subject-index/dict-subject-index-api';
import {
  REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE,
  REGULATION_EUROPEAN_UNION_LAW_SOURCE,
  REGULATION_OTHER_SOURCE,
  REGULATION_SOURCE,
} from '@modules/es-file/es-file-hooked-sources';
import { RegulationField } from '@composite/regulations/regulation-field';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { FormLocalTree } from '@components/form/tree/form-local-tree';
import { TreeDisabledComponent } from '@components/form/tree/tree-disabled-component';
import { RegulationType, Role, UIElement } from '@enums';

export function IndexesFields({
  hasPermission,
}: {
  hasPermission: (
    element: string,
    skipAssignmentCheck?: boolean,
    skipAssignmentRoles?: Role[]
  ) => boolean;
}) {
  const { hookedSources } = useContext(EvidenceContext);
  const { isLocked, mode } = useContext(DetailContext);

  const { regulationTypes, regulationEntityTypes } =
    useContext(StaticDataContext);

  const disabledRegulation =
    isLocked ||
    mode !== DetailMode.VIEW ||
    !hasPermission(
      UIElement.EsFileCommon.REGULATIONS,
      undefined,
      [Role.ANALYST]
    );
  const regulationSource = hookedSources[REGULATION_SOURCE]?.source;
  const constitutionalRegulationSource =
    hookedSources[
      REGULATION_CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES_SOURCE
    ]?.source;
  const europeanUnionRegulationSource =
    hookedSources[REGULATION_EUROPEAN_UNION_LAW_SOURCE]?.source;
  const otherRegulationSource = hookedSources[REGULATION_OTHER_SOURCE]?.source;
  const canEditProceedingsSubjects = hasPermission(
    UIElement.EsFileCommon.PROCEEDINGS_SUBJECTS,
    undefined,
    [Role.ANALYST]
  );
  const canEditSubjectIndexes = hasPermission(
    UIElement.EsFileCommon.SUBJECT_INDEXES,
    undefined,
    [Role.ANALYST]
  );

  return (
    <>
      <FormPanel
        label={
          <FormattedMessage
            id="ES__ES_FILES__DETAIL__PANEL_TITLE__PROCEEDINGS_SUBJECTS"
            defaultMessage="Předmět řízení"
          />
        }
      >
        <FormLocalTree
          name="proceedingsSubjects"
          label={
            <FormattedMessage
              id="ES__ES_FILES__DETAIL__FIELD_LABEL__PROCEEDINGS_SUBJECTS"
              defaultMessage="Předmět řízení"
            />
          }
          labelOptions={{
            hide: true,
          }}
          url={proceedingsSubjectsLocalTreeUrl}
          disabled={!canEditProceedingsSubjects}
          DisabledComponent={TreeDisabledComponent}
        />
      </FormPanel>

      <FormPanel
        label={
          <FormattedMessage
            id="ES__ES_FILES__DETAIL__PANEL_TITLE__SUBJECT_INDEXES"
            defaultMessage="Věcný rejstřík"
          />
        }
      >
        <FormLocalTree
          name="subjectIndexes"
          label={
            <FormattedMessage
              id="ES__ES_FILES__DETAIL__FIELD_LABEL__SUBJECT_INDEXES"
              defaultMessage="Věcný rejstřík"
            />
          }
          labelOptions={{
            hide: true,
          }}
          url={subjectIndexesLocalTreeUrl}
          disabled={!canEditSubjectIndexes}
          DisabledComponent={TreeDisabledComponent}
        />
      </FormPanel>

      <RegulationField
        regulationTypes={regulationTypes}
        regulationEntityTypes={regulationEntityTypes}
        disabled={disabledRegulation}
        source={regulationSource}
        typeSources={{
          [RegulationType.CONSTITUTIONAL_LAWS_AND_INTERNATIONAL_TREATIES]:
            constitutionalRegulationSource,
          [RegulationType.EUROPEAN_UNION_LAW]: europeanUnionRegulationSource,
          [RegulationType.OTHER]: otherRegulationSource,
        }}
      />
    </>
  );
}
